import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2 } from "lucide-react";
import api from "../lib/api";
import { Ornament } from "../components/Ornament";

const GIFT_OPTIONS = [
  { sku: "individual", name: "The Living Book",        headline: "A year of their stories.",     price: 49 },
  { sku: "family",     name: "The Living Book Family", headline: "For the whole family.",        price: 89 },
];

export default function GiftPage() {
  const navigate = useNavigate();
  const [plan, setPlan] = useState("individual");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [giftMessage, setGiftMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try { await api.get("/auth/me"); } catch (e) { navigate("/login"); }
    })();
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const res = await api.post("/gifts/checkout", {
        sku: plan,
        recipient_email: recipientEmail,
        recipient_name: recipientName || null,
        gift_message: giftMessage || null,
        origin_url: window.location.origin,
      });
      if (res.data?.url) window.location.href = res.data.url;
      else setError("Could not start checkout.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = Array.isArray(d) ? (d[0]?.msg || "Please check your details.") : (typeof d === "string" ? d : "Something went wrong.");
      setError(msg);
    } finally { setBusy(false); }
  };

  const selected = GIFT_OPTIONS.find((s) => s.sku === plan);

  return (
    <div className="min-h-screen paper" data-testid="gift-page">
      <header className="max-w-3xl mx-auto px-6 sm:px-10 py-8">
        <Link to="/subscribe" className="flex items-center gap-2 label-ui text-[11px] text-[#8C8278] hover:text-[#111D33] transition-colors" data-testid="back-link">
          <ChevronLeft size={16} /> Back
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-6">
        <div className="text-center mb-12">
          <p className="label-ui text-[11px] text-[#B8973A]">A gift of remembering</p>
          <h1 className="text-4xl sm:text-5xl mt-4" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Give them a <span style={{ fontStyle: "italic" }}>year of their stories.</span>
          </h1>
          <Ornament className="my-6" />
          <p className="italic text-[#8C8278] max-w-xl mx-auto" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.1rem" }}>
            We'll send them a gentle email with a code. They'll create their own account, and a year of The Living Book will already be theirs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          {GIFT_OPTIONS.map((s) => (
            <button key={s.sku} type="button" onClick={() => setPlan(s.sku)}
              className={`text-left p-6 transition-all duration-500 ${plan === s.sku ? "bg-white" : "bg-[#FAF7F2] hover:bg-white"}`}
              style={{ borderRadius: 4, border: plan === s.sku ? "1px solid #B8973A" : "1px solid #F0EAE0" }}
              data-testid={`gift-card-${s.sku}`}>
              <h3 className="text-xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>{s.name}</h3>
              <p className="italic text-[#8C8278] text-sm" style={{ fontFamily: "Cormorant Garamond, serif" }}>{s.headline}</p>
              <div className="text-2xl mt-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>£{s.price}<span className="text-sm text-[#8C8278]"> · 1 year</span></div>
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="bg-white hairline-cream p-8" style={{ borderRadius: 4 }} data-testid="gift-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="label-ui text-[10px] text-[#8C8278] block mb-2">Their name (optional)</label>
              <input value={recipientName} onChange={(e) => setRecipientName(e.target.value)}
                className="w-full px-4 py-3 hairline-cream bg-white" style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem", minHeight: 48 }}
                data-testid="gift-recipient-name" />
            </div>
            <div>
              <label className="label-ui text-[10px] text-[#8C8278] block mb-2">Their email</label>
              <input type="email" required value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)}
                className="w-full px-4 py-3 hairline-cream bg-white" style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem", minHeight: 48 }}
                data-testid="gift-recipient-email" />
            </div>
          </div>
          <div className="mb-6">
            <label className="label-ui text-[10px] text-[#8C8278] block mb-2">A note from you (optional)</label>
            <textarea value={giftMessage} onChange={(e) => setGiftMessage(e.target.value)} rows={4}
              placeholder="Mum — I want to read your stories one day. With love."
              className="w-full px-4 py-3 hairline-cream bg-white" style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "1rem", lineHeight: 1.6 }}
              data-testid="gift-message" />
          </div>
          {error && <p className="text-red-700 text-sm mb-4" data-testid="gift-error">{error}</p>}
          <button type="submit" disabled={busy} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="gift-checkout-btn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : null}
            Continue to payment · £{selected.price}
          </button>
        </form>
      </main>
    </div>
  );
}
